// Чистая логика клиента без DOM: подбор новых фраз, очереди, фильтры словаря.
// Расписание здесь НЕ вычисляется — только читаются сроки, полученные с сервера.

import type { Example, ExampleInput, LearningEvent, PoolOrder, Problem, Rating, Reason, Report, Settings, Unit, UnitInput } from './types.ts';

export const KIND_LABELS: Record<string, string> = {
  word: 'Слово',
  expression: 'Выражение',
  pattern: 'Речевой шаблон',
  connector: 'Разговорная связка',
};

export function mainExample(u: Unit) {
  return u.examples.find((e) => e.role === 'main') ?? u.examples[0];
}

/** Аудиопримеры единицы по порядку слотов. */
export function listenExamples(u: Unit): Example[] {
  return u.examples.filter((e) => e.role === 'listen').sort((a, b) => (a.slot ?? 0) - (b.slot ?? 0));
}

/** Локальные события, которые сервер ещё не учёл в снимке. */
export interface Overlay {
  introduced: Set<string>;
  /** Оценены по тексту. */
  reviewed: Set<string>;
  /** Оценены по слуху. */
  listened: Set<string>;
  /** Отмечена ошибка в примере: id версии примера → тип проблемы. */
  flagged: Map<string, Problem>;
}

export function buildOverlay(events: LearningEvent[]): Overlay {
  const o: Overlay = { introduced: new Set(), reviewed: new Set(), listened: new Set(), flagged: new Map() };
  for (const e of events) {
    if (e.type === 'intro') o.introduced.add(e.unitId);
    if (e.type === 'review') (e.track === 'listen' ? o.listened : o.reviewed).add(e.unitId);
    if (e.type === 'example_flagged' && e.exampleId) o.flagged.set(e.exampleId, e.problem ?? '');
  }
  return o;
}

/** Открытая жалоба на пример: с сервера или отмеченная на устройстве. */
export function exampleReport(e: Example, o: Overlay): Report | null {
  if (e.report) return e.report;
  const p = o.flagged.get(e.id);
  return p === undefined ? null : { problem: p, createdAt: '' };
}

/** Первая открытая жалоба на любой текущий пример единицы (метка «пример с ошибкой» в словаре). */
export function reportOf(u: Unit, o: Overlay): Report | null {
  for (const e of u.examples) {
    const r = exampleReport(e, o);
    if (r) return r;
  }
  return null;
}

export const PROBLEM_LABELS: Record<Exclude<Problem, ''>, string> = {
  pronunciation: 'Произношение или ударение',
  unnatural: 'Звучит неестественно',
  text_error: 'Ошибка в тексте или переводе',
};

export const REASON_LABELS: Record<Reason, string> = {
  unknown_phrase: 'Не знал фразу',
  not_recognized: 'Не узнал на слух',
  too_fast: 'Не успел понять',
};

/** Единица в пуле «к изучению» с учётом локального знакомства. */
export function inPool(u: Unit, o: Overlay): boolean {
  return u.status === 'pool' && !o.introduced.has(u.id);
}

export function isLearning(u: Unit, o: Overlay): boolean {
  return u.status === 'learning' || o.introduced.has(u.id);
}

export function poolStats(units: Unit[], o: Overlay) {
  const pool = units.filter((u) => inPool(u, o));
  const own = pool.filter((u) => u.origin === 'own').length;
  return { total: pool.length, own, fromSets: pool.length - own };
}

/**
 * Новые фразы для знакомства: min(N, размер пула).
 * Порядок задаётся настройкой; внутри группы — от старых к новым.
 * Это единственная функция выбора, её можно заменить целиком.
 */
export function pickNew(units: Unit[], o: Overlay, settings: Settings, rng: () => number = Math.random): Unit[] {
  const pool = units.filter((u) => inPool(u, o)).sort(byCreated);
  const n = Math.max(0, Math.min(settings.newPerSession, pool.length));
  return orderPool(pool, settings.poolOrder, rng).slice(0, n);
}

function orderPool(pool: Unit[], order: PoolOrder, rng: () => number): Unit[] {
  const own = pool.filter((u) => u.origin === 'own');
  const sets = pool.filter((u) => u.origin !== 'own');
  switch (order) {
    case 'own_first':
      return [...own, ...sets];
    case 'sets_first':
      return [...sets, ...own];
    case 'random': {
      const a = [...pool];
      for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(rng() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
      }
      return a;
    }
  }
}

function byCreated(a: Unit, b: Unit) {
  return a.createdAt < b.createdAt ? -1 : a.createdAt > b.createdAt ? 1 : a.id < b.id ? -1 : 1;
}

/**
 * Текстовая очередь: срок не позже now, от самой просроченной.
 * Карточки, уже оценённые локально (сервер ещё не прислал новый срок), исключаются.
 */
export function textQueue(units: Unit[], o: Overlay, now: Date): Unit[] {
  const iso = now.toISOString();
  return units
    .filter((u) => u.text && u.text.due <= iso && !o.reviewed.has(u.id))
    .sort((a, b) => (a.text!.due < b.text!.due ? -1 : a.text!.due > b.text!.due ? 1 : 0));
}

/** Где доступны записи: сеть есть — любые, иначе только сохранённые на устройстве. */
export interface AudioAvailability {
  online: boolean;
  cached: Set<string>;
}

/**
 * Примеры, которые может сыграть аудиокарточка: аудиопримеры с записью, без жалобы и доступные
 * (сеть или кэш). Если таких нет — основной пример по тем же правилам. Пусто — карточки нет.
 */
export function listenCandidates(u: Unit, o: Overlay, av: AudioAvailability): Example[] {
  const usable = (e: Example) => !!e.audio && !exampleReport(e, o) && (av.online || av.cached.has(e.audio));
  const listen = listenExamples(u).filter(usable);
  if (listen.length) return listen;
  const m = mainExample(u);
  return m && m.role === 'main' && usable(m) ? [m] : [];
}

/** Аудиокарточка отложена жалобами: есть открытая жалоба, и ни один пример с записью не пригоден. */
export function listenPaused(u: Unit, o: Overlay): boolean {
  return !!reportOf(u, o) && listenCandidates(u, o, { online: true, cached: new Set() }).length === 0;
}

/**
 * Аудиоочередь: срок по слуху не позже now и есть хотя бы один пример, который можно сыграть
 * (listenCandidates). Оценённые локально исключаются. От самой просроченной.
 */
export function listenQueue(units: Unit[], o: Overlay, now: Date, av: AudioAvailability): Unit[] {
  const iso = now.toISOString();
  return units
    .filter((u) => u.listen && u.listen.due <= iso && !o.listened.has(u.id) && listenCandidates(u, o, av).length > 0)
    .sort((a, b) => (a.listen!.due < b.listen!.due ? -1 : a.listen!.due > b.listen!.due ? 1 : 0));
}

export type DictFilter = 'all' | 'learning' | 'pool' | 'review' | 'hard' | 'flagged';

export const DICT_FILTERS: [DictFilter, string][] = [
  ['all', 'Все'],
  ['learning', 'Изучаются'],
  ['pool', 'В пуле'],
  ['review', 'На проверке'],
  ['hard', 'Трудные'],
  ['flagged', 'С ошибкой'],
];

/** Ждёт действий пользователя после генерации: «на проверке» или «не удалось подготовить». */
export function needsReview(u: Unit): boolean {
  return u.status === 'review' || u.status === 'gen_failed';
}

/** Куда ведёт фраза в словаре: экран проверки, подробный вид или никуда (пока готовится). */
export function dictTarget(u: Unit): string | null {
  if (u.status === 'generating') return null;
  return (needsReview(u) ? '#/review/' : '#/unit/') + encodeURIComponent(u.id);
}

/** Причина сбоя генерации простыми словами (коды сервера, internal/gen). */
export const GEN_ERROR_LABELS: Record<string, string> = {
  timeout: 'Сервис генерации не ответил вовремя.',
  network: 'Не удалось связаться с сервисом генерации.',
  auth: 'Сервис генерации отказал в доступе — проверьте ключ и модель на сервере.',
  balance: 'На счёте сервиса генерации закончились средства.',
  provider: 'Сервис генерации вернул ошибку.',
  invalid_output: 'Модель вернула некорректный результат.',
  interrupted: 'Генерация прервалась.',
};

export function genErrorText(code: string | null): string {
  return (code && GEN_ERROR_LABELS[code]) || 'Не удалось подготовить карточку.';
}

/**
 * Опрос снимка, пока фраза готовится: паузы растут, потом опрос прекращается
 * (дальше снимок обновится при открытии словаря). null — опрашивать не нужно.
 */
export const GEN_POLL_DELAYS = [2000, 5000, 10000, 20000, 40000];

export function genPollDelay(units: Unit[], step: number): number | null {
  if (!units.some((u) => u.status === 'generating')) return null;
  return GEN_POLL_DELAYS[step] ?? null;
}

/** Поиск по форме и значению без учёта регистра плюс фильтр по статусу. */
export function filterDict(units: Unit[], o: Overlay, filter: DictFilter, q: string): Unit[] {
  const needle = q.trim().toLocaleLowerCase('ru');
  return units
    .filter((u) => {
      if (filter === 'learning' && !isLearning(u, o)) return false;
      if (filter === 'pool' && !inPool(u, o)) return false;
      if (filter === 'review' && !needsReview(u)) return false;
      if (filter === 'hard' && !u.isHard) return false;
      if (filter === 'flagged' && !reportOf(u, o)) return false;
      if (!needle) return true;
      return u.form.toLocaleLowerCase('ru').includes(needle) || u.meaning.toLocaleLowerCase('ru').includes(needle);
    })
    .sort((a, b) => (a.createdAt > b.createdAt ? -1 : a.createdAt < b.createdAt ? 1 : 0));
}

/**
 * Что сделает выключение набора: фразы без знакомства уйдут из пула и словаря,
 * изучаемые (в том числе познакомленные на устройстве) останутся.
 */
export function setDisableCounts(units: Unit[], o: Overlay, setId: string): { leave: number; stay: number } {
  const mine = units.filter((u) => u.setId === setId);
  const stay = mine.filter((u) => isLearning(u, o)).length;
  return { leave: mine.length - stay, stay };
}

const DAY = 24 * 3600 * 1000;

/** Короткая человеческая подпись к сроку трека. */
export function trackLabel(t: { due: string; reps: number } | null, now: Date, closedText: string): string {
  if (!t) return closedText;
  const due = new Date(t.due).getTime();
  const diff = due - now.getTime();
  const when =
    diff <= 0 ? 'сегодня'
    : diff < 3600 * 1000 ? 'меньше чем через час'
    : diff < DAY ? `через ${Math.round(diff / 3600 / 1000)} ч`
    : `через ${Math.round(diff / DAY)} дн.`;
  return t.reps === 0 ? `новая, ${when}` : `повторений: ${t.reps}, следующее ${when}`;
}

/**
 * Нужна ли метка значения на лицевой стороне карточки: только если у пользователя есть другая
 * единица с той же формой. У однозначной фразы метка лишь подсказывает ответ.
 */
export function senseOnFront(u: Unit, units: Unit[]): boolean {
  if (!u.senseLabel) return false;
  const norm = (s: string) => s.trim().toLocaleLowerCase('en');
  const f = norm(u.form);
  return units.some((x) => x.id !== u.id && norm(x.form) === f);
}

/** Выделяет целевой фрагмент в тексте примера (без учёта регистра). Возвращает HTML. */
export function highlight(en: string, target: string): string {
  const i = target ? en.toLowerCase().indexOf(target.toLowerCase()) : -1;
  if (i < 0) return esc(en);
  return esc(en.slice(0, i)) + '<mark>' + esc(en.slice(i, i + target.length)) + '</mark>' + esc(en.slice(i + target.length));
}

/** Форма существительного по числу: 1 фраза, 2 фразы, 5 фраз (11–14 — третья форма). */
export function plural(n: number, forms: [string, string, string]): string {
  const m10 = n % 10, m100 = n % 100;
  if (m10 === 1 && m100 !== 11) return forms[0];
  if (m10 >= 2 && m10 <= 4 && (m100 < 12 || m100 > 14)) return forms[1];
  return forms[2];
}

/** Разбивка пула на «Сегодня»: 1 своя, 2 свои, 5 своих. */
export const OWN: [string, string, string] = ['своя', 'свои', 'своих'];

export function esc(s: string): string {
  return s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');
}

/** При стольких карточках в аудиоочереди знакомство с новыми фразами блокируется. */
export const LISTEN_LIMIT = 30;

/** Что показывает экран «Сегодня»: созревшие карточки, пул и кнопка знакомства. */
export interface TodaySummary {
  textDue: number;
  listenDue: number;
  pool: { total: number; own: number; fromSets: number };
  introCount: number;
  introBlocked: '' | 'empty_pool' | 'disabled' | 'audio_overflow';
}

export function todaySummary(
  units: Unit[], o: Overlay, settings: Settings, now: Date,
  av: AudioAvailability = { online: true, cached: new Set() },
): TodaySummary {
  const pool = poolStats(units, o);
  const introCount = Math.max(0, Math.min(settings.newPerSession, pool.total));
  const listenDue = listenQueue(units, o, now, av).length;
  return {
    textDue: textQueue(units, o, now).length,
    listenDue,
    pool,
    introCount,
    introBlocked: pool.total === 0 ? 'empty_pool'
      : settings.newPerSession <= 0 ? 'disabled'
      : listenDue >= LISTEN_LIMIT ? 'audio_overflow'
      : '',
  };
}

/** Состояние аудиокарточки на экране. */
export interface ListenCard {
  eventId: string;
  /** Сыгранный пример, выбранный при показе карточки; не меняется до оценки (null — играть нечего). */
  example: Example | null;
  flipped: boolean;
  /** Прослушивания до «Показать ответ». */
  plays: number;
  /** Прослушивания после ответа. */
  replays: number;
  /** Переключатель «Медленнее 0.75×». */
  slow: boolean;
  /** Звучала ли запись с замедлением. */
  slowUsed: boolean;
  reasons: Reason[];
  /** Включена отметка «Ошибка в примере». */
  flag: boolean;
  problem: Problem;
}

/**
 * Новая аудиокарточка: пример выбирается случайно среди listenCandidates, какие уже показывались,
 * не учитывается. rng — параметр, чтобы тесты были детерминированными.
 */
export function newListenCard(eventId: string, u: Unit | undefined, o: Overlay, av: AudioAvailability, rng: () => number = Math.random): ListenCard {
  const c = u ? listenCandidates(u, o, av) : [];
  const example = c.length ? c[Math.min(c.length - 1, Math.floor(rng() * c.length))] : null;
  return { eventId, example, flipped: false, plays: 0, replays: 0, slow: false, slowUsed: false, reasons: [], flag: false, problem: '' };
}

/** Нажатие «прослушать» на аудиокарточке: до ответа и после считаются отдельно. */
export function countPlay(c: ListenCard): void {
  if (c.flipped) c.replays++;
  else c.plays++;
  if (c.slow) c.slowUsed = true;
}

/** Событие проверки по слуху — с версией и записью сыгранного примера. */
export function listenReviewEvent(c: ListenCard, u: Unit, rating: Rating, time: string): LearningEvent {
  const ex = c.example;
  return {
    id: c.eventId, type: 'review', unitId: u.id, exampleId: ex?.id, track: 'listen', time, rating,
    plays: c.plays, replaysAfter: c.replays, slowed: c.slowUsed,
    reasons: (Object.keys(REASON_LABELS) as Reason[]).filter((r) => c.reasons.includes(r)),
    recordingKey: ex?.audio ?? undefined,
  };
}

/** Событие «Ошибка в примере» на сыгранном примере: без оценки, расписание не меняется. */
export function flagEvent(c: ListenCard, u: Unit, time: string): LearningEvent {
  const ex = c.example;
  return {
    id: c.eventId, type: 'example_flagged', unitId: u.id, exampleId: ex?.id, track: 'listen', time,
    recordingKey: ex?.audio ?? undefined, problem: c.problem,
  };
}

/** Сколько дней вперёд хранить на устройстве записи созревающих единиц. */
export const CACHE_DAYS = 3;

/**
 * Записи, которые нужны на устройстве: единицы со сроком по любому треку в ближайшие CACHE_DAYS дней
 * и единицы пула, которые возьмёт следующее знакомство (при случайном порядке — весь пул).
 * Берутся записи фразы, основного примера и всех аудиопримеров — офлайн аудиокарточке есть из чего выбирать.
 */
export function audioKeysToCache(units: Unit[], o: Overlay, settings: Settings, now: Date): Set<string> {
  const horizon = new Date(now.getTime() + CACHE_DAYS * DAY).toISOString();
  const soon = (t: { due: string } | null) => !!t && t.due <= horizon;
  const pick = settings.poolOrder === 'random'
    ? units.filter((u) => inPool(u, o))
    : pickNew(units, o, settings);
  const chosen = new Set(pick.map((u) => u.id));
  const keys = new Set<string>();
  for (const u of units) {
    if (!(soon(u.text) || soon(u.listen) || chosen.has(u.id) || (isLearning(u, o) && !u.text))) continue;
    if (u.phraseAudio) keys.add(u.phraseAudio);
    for (const e of u.examples) if (e.audio) keys.add(e.audio);
  }
  return keys;
}

/** Сколько аудиопримеров можно задать вручную. */
export const MAX_LISTEN = 4;

const filled = (e: ExampleInput | null | undefined) => !!e && [e.en, e.ru, e.target].some((s) => s.trim());

/**
 * Проверка формы единицы до отправки — те же правила, что на сервере
 * (сервер всё равно проверяет сам; ключи полей совпадают с его ответом: listen.N.* — N = слот 1–4).
 */
export function validateUnitInput(u: UnitInput): Record<string, string> {
  const errs: Record<string, string> = {};
  const req = (k: string, v: string) => {
    if (!v.trim()) errs[k] = 'Обязательное поле';
  };
  if (!KIND_LABELS[u.kind]) errs.kind = 'Выберите тип';
  req('form', u.form);
  req('meaning', u.meaning);
  req('explanation', u.explanation);
  req('main.en', u.main.en);
  req('main.ru', u.main.ru);
  req('main.target', u.main.target);
  const notIn = (ex: { en: string; target: string }) =>
    ex.en.trim() && ex.target.trim() && !ex.en.toLowerCase().includes(ex.target.trim().toLowerCase());
  if (notIn(u.main)) errs['main.target'] = 'Этого фрагмента нет в тексте примера';
  const norm = (s: string) => s.trim().toLowerCase();
  const seen = new Map<string, string>();
  if (u.main.en.trim()) seen.set(norm(u.main.en), 'основным примером');
  u.listen.forEach((e, i) => {
    if (!e || !filled(e)) return;
    const p = `listen.${i + 1}`;
    (['en', 'ru', 'target'] as const).forEach((k) => {
      if (!e[k].trim()) errs[`${p}.${k}`] = 'Заполните аудиопример полностью или оставьте его пустым';
    });
    if (notIn(e)) errs[`${p}.target`] = 'Этого фрагмента нет в тексте примера';
    if (e.en.trim()) {
      const what = seen.get(norm(e.en));
      if (what) errs[`${p}.en`] = `Совпадает с ${what}`;
      else seen.set(norm(e.en), `аудиопримером ${i + 1}`);
    }
  });
  return errs;
}

/** Форма в виде, пригодном для отправки: пустые аудиопримеры — null, пустые слоты в конце убраны. */
export function normalizeUnitInput(u: UnitInput): UnitInput {
  const listen = u.listen.slice(0, MAX_LISTEN).map((e) => (filled(e) ? e : null));
  while (listen.length && listen[listen.length - 1] === null) listen.pop();
  return { ...u, listen };
}

/** Текст для озвучки фразы: без многоточия шаблона («I was going to…»). */
export function speakable(form: string): string {
  return form.replace(/…|\.\.\./g, '').trim();
}

// ——— push-уведомления ———

/** Что известно об уведомлениях на этом устройстве. */
export interface PushEnv {
  /** Есть service worker, PushManager и Notification (на iPhone — только в приложении с экрана «Домой»). */
  supported: boolean;
  /** На сервере задан ключ VAPID (снимок: push.enabled). */
  serverEnabled: boolean;
  permission: NotificationPermission;
  /** У этого устройства есть подписка; null — ещё не известно. */
  subscribed: boolean | null;
  online: boolean;
}

export interface PushView {
  state: 'unsupported' | 'server_off' | 'denied' | 'offline' | 'on' | 'off';
  /** Переключатель включён (подписка устройства есть). */
  on: boolean;
  /** Переключатель можно нажать. */
  canToggle: boolean;
  /** Пояснение под переключателем; пусто — не нужно. */
  note: string;
}

/** Состояние переключателя «Уведомления о текстовых повторениях» и пояснение к нему. */
export function pushView(e: PushEnv): PushView {
  const on = !!e.subscribed;
  if (!e.supported) {
    return { state: 'unsupported', on: false, canToggle: false,
      note: 'Этот браузер не поддерживает уведомления. На iPhone они работают в приложении, установленном на экран «Домой»: «Поделиться» → «На экран „Домой“».' };
  }
  if (!e.serverEnabled) return { state: 'server_off', on: false, canToggle: false, note: 'На сервере уведомления не настроены.' };
  if (e.permission === 'denied') {
    return { state: 'denied', on: false, canToggle: false,
      note: 'Уведомления запрещены в настройках браузера для этого сайта. Разрешите их там и вернитесь сюда.' };
  }
  if (!e.online) return { state: 'offline', on, canToggle: false, note: 'Нет сети. Включать и выключать уведомления можно только при подключении к серверу.' };
  return { state: on ? 'on' : 'off', on, canToggle: e.subscribed !== null, note: '' };
}

/** Проверка полей уведомлений в форме настроек (сервер проверяет то же самое). */
export function validatePushSettings(s: { quietStart: string; quietEnd: string; maxPushPerDay: number }): Record<string, string> {
  const errors: Record<string, string> = {};
  const hhmm = /^([01]\d|2[0-3]):[0-5]\d$/;
  if (!hhmm.test(s.quietStart)) errors.quietStart = 'Время в формате ЧЧ:ММ';
  if (!hhmm.test(s.quietEnd)) errors.quietEnd = 'Время в формате ЧЧ:ММ';
  else if (s.quietStart === s.quietEnd) errors.quietEnd = 'Начало и конец тихих часов должны различаться';
  if (!Number.isInteger(s.maxPushPerDay) || s.maxPushPerDay < 1 || s.maxPushPerDay > 10) errors.maxPushPerDay = 'Допустимо от 1 до 10';
  return errors;
}
